import { action, observable, makeObservable, computed } from 'mobx';
import { Notify } from '$src/utils/notify';
import { controlCenterApi } from '$src/api/control-center-api';
import eventsToIconsMap from '../pages/EventSummary/EventsToIconsMap';

export default class EventStore {
  loading = false;
  eventSummary = {};

  setLoading = (value) => {
    this.loading = value;
  };

  setEventSummary = (summary) => {
    this.eventSummary = summary;
  };

  fetchEventSummary = async (userId, locationId) => {
    try {
      this.setLoading(true);
      const eventSummary = await controlCenterApi.fetchEventSummary(userId, locationId);

      // We don't want to show the Suspicious tile, instead we should add those events to unknown person as per
      // product request. See https://jira.atl.ring.com/browse/HOP-6518
      const tempEventSummary = { ...eventSummary, resolvedEvents: { count: eventSummary?.resolvedEvents?.count } };
      eventSummary?.resolvedEvents?.resolutionTypes?.forEach(({ resolutionType, count }) => {
        tempEventSummary.resolvedEvents[resolutionType] = count;
      });
      tempEventSummary.resolvedEvents.UnknownPerson =
        (tempEventSummary.resolvedEvents.RequireAttention || 0) + (tempEventSummary.resolvedEvents.UnknownPerson || 0);
      this.setEventSummary(tempEventSummary);
    } catch (e) {
      Notify.error('There was an error getting event summary', e);
    } finally {
      this.setLoading(false);
    }
  };

  fetchEvents = async (userId, locationId, startTime, limit, nextToken, resolutionTypes) => {
    try {
      return await controlCenterApi.fetchEvents(userId, locationId, startTime, limit, nextToken, resolutionTypes);
    } catch (e) {
      Notify.error('There was an error getting events', e);
    }
  };

  fetchEventsTimeseries = async (userId, locationId) => {
    try {
      return await controlCenterApi.fetchEventsTimeseries(userId, locationId);
    } catch (e) {
      Notify.error('There was an error getting events timeseries', e);
    }
  };

  fetchEmergenciesTimeseries = async (userId, locationId) => {
    try {
      return await controlCenterApi.fetchEventsTimeseries(userId, locationId, 'Medical,Fire,Police');
    } catch (e) {
      Notify.error('There was an error getting emergencies timeseries', e);
    }
  };

  getResolvedEvent = (eventType) => this.eventSummary?.resolvedEvents?.[eventType] || 0;

  get eventPercentage() {
    const overagesThreshold = this.rootStore.locationContextStore.location?.overagesThreshold;

    const resolvedEvents = this.eventSummary?.resolvedEvents?.count || 0;
    return Math.min(100, overagesThreshold !== 0 ? Math.floor((resolvedEvents * 100) / overagesThreshold) : 0);
  }

  // Guard reviewed event count is the difference between all our resolved event types and the resolved event counts.
  get guardReviewedEvents() {
    const mappedEvents = Object.keys(this.eventSummary?.resolvedEvents || {}).map((element) => {
      if (element && eventsToIconsMap[element]) return this.eventSummary.resolvedEvents[element];
      return 0;
    });
    const sum = mappedEvents?.reduce((accumulator, current) => accumulator + current, 0) || 0;
    const resolvedEvents = this.eventSummary.resolvedEvents?.count || 0;
    return resolvedEvents >= sum ? resolvedEvents - sum : 0;
  }

  get resolvedEvents() {
    return this.eventSummary?.resolvedEvents?.count ?? 0;
  }

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      loading: observable,
      eventSummary: observable,
      setLoading: action,
      fetchEventSummary: action,
      fetchEventsTimeseries: action,
      fetchEmergenciesTimeseries: action,
      fetchEvents: action,
      setEventSummary: action,
      eventPercentage: computed,
      guardReviewedEvents: computed,
      resolvedEvents: computed,
    });
  }
}
